import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
	ResponsiveContainer,
	LineChart,
	Line,
	XAxis,
	YAxis,
	Tooltip,
	CartesianGrid,
	Area,
	AreaChart,
} from 'recharts';
import { Sparkles, Repeat, ShieldCheck, Trophy } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import Layout from '@/components/Layout';

const ICONS = { Sparkles, Repeat, ShieldCheck, Trophy };

const MONTH_LABEL = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function ProgressPage() {
	const [data, setData] = useState({ months: [], badges: [], assessments_count: 0 });
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get('/progress');
				setData(data || { months: [], badges: [] });
			} catch (err) {
				toast.error(formatApiError(err));
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	const chart = (data.months || []).map((m) => ({
		label: `${MONTH_LABEL[m.month - 1]} ${String(m.year).slice(-2)}`,
		score: m.score,
	}));

	return (
		<Layout>
			<div className="enter-stagger space-y-8">
				<div>
					<div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Your Journey</div>
					<h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold tracking-tight text-slate-900">Monthly Progress</h1>
					<p className="mt-2 text-slate-600">Tracking how your family becomes more prepared, month by month.</p>
				</div>

				<div className="card-soft p-6 sm:p-8">
					<div className="flex items-center justify-between mb-4">
						<div className="font-heading text-xl font-semibold text-slate-900">Resilience over time</div>
						<div className="text-sm text-slate-500">{data.assessments_count} assessment{data.assessments_count === 1 ? '' : 's'} completed</div>
					</div>
					{chart.length === 0 ? (
						<div className="py-10 text-center text-slate-500">
							Your progress graph will appear after your first assessment.
						</div>
					) : (
						<div className="h-72">
							<ResponsiveContainer width="100%" height="100%">
								<AreaChart data={chart} margin={{ top: 20, right: 12, left: 0, bottom: 4 }}>
									<defs>
										<linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
											<stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
											<stop offset="100%" stopColor="#10B981" stopOpacity={0} />
										</linearGradient>
									</defs>
									<CartesianGrid stroke="#E2E8F0" strokeDasharray="4 4" vertical={false} />
									<XAxis dataKey="label" stroke="#94A3B8" tickLine={false} axisLine={false} />
									<YAxis domain={[0, 100]} stroke="#94A3B8" tickLine={false} axisLine={false} />
									<Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0' }} />
									<Area type="monotone" dataKey="score" stroke="#10B981" strokeWidth={3} fill="url(#g1)" />
								</AreaChart>
							</ResponsiveContainer>
						</div>
					)}
				</div>

				<div>
					<div className="font-heading text-xl font-semibold text-slate-900 mb-4">Achievements</div>
					<div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
						{(data.badges || []).length === 0 ? (
							<div className="card-soft p-6 col-span-full text-center text-slate-500">
								Complete your first assessment to earn your first badge.
							</div>
						) : (
							(data.badges || []).map((b) => {
								const Icon = ICONS[b.icon] || Sparkles;
								return (
									<div key={b.key} className="card-soft p-5 text-center">
										<div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center">
											<Icon className="w-7 h-7" />
										</div>
										<div className="mt-3 font-heading font-semibold text-slate-900">{b.title}</div>
										<div className="text-xs text-slate-500 mt-1">{b.desc}</div>
									</div>
								);
							})
						)}
					</div>
				</div>
			</div>
		</Layout>
	);
}
